import { DateOnly, differenceInDays } from '@if/core/date'
import { t } from '@if/core/i18n'
import { EPaymentFrequency } from '@if/core/types'
import { dateOnly, InferSubmit, integer, object, ref, text } from '@if/core/yup'
import { type IfUser, useIfUser } from '@if/mui'
import { EPaymentMethod } from '@src/types/constants'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { useMemo } from 'react'
import { previewedSimulation } from '../LoanCalendarActionPreview/LoanCalendarActionPreview-schema'
import { MAX_PAYMENTS_TO_RAISE } from './LoanMoveCalendar-hooks'

/**
 * The simulation and the user are closed over, so the rules read them directly with no
 * validation context. `numberOfPaymentsToRaise` is the field the agent types; the payment
 * ids the action carries are derived from it and `fromDate` (see `getPaymentToRaiseIds`),
 * which is why the two "raise" rules live on the count rather than on a hidden id list.
 */
export const getMoveCalendarSchema = (simulation: CalendarSimulation, user: IfUser, canBypassAcpLock: boolean) =>
  object({
    fromDate: dateOnly()
      .default(null)
      .required()
      .test(
        'test-zeroShiftCase',
        () => t('loan.payment.moveCalendarDialog.errors.zeroDayShift'),
        (value, context) => {
          const toDate = context.resolve<DateOnly>(ref('toDate'))
          if (!toDate || !value) return true // If the to date is null, we can't validate
          return value.getTime() !== toDate.getTime()
        },
      )
      .test(
        'test-validMinimumFromDate',
        () => t('loan.payment.moveCalendarDialog.errors.invalidFromDate'),
        (value) => {
          if (!value) return true
          return value >= simulation.getNextValidActionDate(EPaymentMethod.PreAuthorizedDebit, canBypassAcpLock)
        },
      )
      .test(
        'test-validPaymentDate',
        () => t('loan.payment.moveCalendarDialog.errors.invalidPaymentDate'),
        (value) => {
          if (!value) return true
          const operations = simulation.operationInformations
          if (!operations) return true
          return operations.some(
            (oi) => oi.isCustomerPaymentOperation() && oi.operation.effectiveDate.getTime() === value.getTime(),
          )
        },
      ),
    toDate: dateOnly()
      .default(null)
      .required()
      .test(
        'test-validMinimumToDate',
        () => t('loan.payment.moveCalendarDialog.errors.invalidToDate'),
        (value) => {
          if (!value) return true
          return value >= simulation.getNextValidActionDate(EPaymentMethod.PreAuthorizedDebit, canBypassAcpLock)
        },
      )
      .test(
        'test-shiftBeforeActivationDate',
        () => t('loan.payment.moveCalendarDialog.errors.beforeActivationDateToDate'),
        (value) => value > simulation.fullLoan.loan.activationOn,
      )
      .test(
        'test-zeroShiftCase',
        () => t('loan.payment.moveCalendarDialog.errors.zeroDayShift'),
        (value, context) => {
          const fromDate = context.resolve<DateOnly>(ref('fromDate'))
          if (!fromDate || !value) return true // If the to date is null, we can't validate
          return value.getTime() !== fromDate.getTime()
        },
      )
      .test(
        'test-monthlyTooFarShift',
        () => t('loan.payment.moveCalendarDialog.errors.monthlyTooFarShift'),
        (value, context) => {
          const fromDate = context.resolve<DateOnly>(ref('fromDate'))
          if (!fromDate || !value) return true // If the to date is null, we can't validate
          if (simulation.getRepaymentConfigAtDate(fromDate)?.frequency !== EPaymentFrequency.Monthly) return true
          return differenceInDays(value, fromDate) <= 30
        },
      )
      .test(
        'test-biweeklyTooFarShift',
        () => t('loan.payment.moveCalendarDialog.errors.biweeklyTooFarShift'),
        (value, context) => {
          const fromDate = context.resolve<DateOnly>(ref('fromDate'))
          if (!fromDate || !value) return true // If the to date is null, we can't validate
          if (simulation.getRepaymentConfigAtDate(fromDate)?.frequency !== EPaymentFrequency.BiWeekly) return true
          return differenceInDays(value, fromDate) <= 7
        },
      )
      .test(
        'test-movingBeforeNextPayment',
        () => t('loan.payment.moveCalendarDialog.errors.shiftAfterNextPayment'),
        (value, context) => {
          const fromDate = context.resolve<DateOnly>(ref('fromDate'))
          if (!fromDate || !value) return true // If the to date is null, we can't validate
          const nextPayment = simulation.getNextPaymentAfterDate(fromDate)
          if (!nextPayment) return true

          return nextPayment.effectiveDate.getTime() >= value.getTime()
        },
      ),

    reason: text().required(),

    numberOfPaymentsToRaise: integer()
      .default(0)
      .required()
      .min(0)
      .max(MAX_PAYMENTS_TO_RAISE)
      .test(
        'test-unnecessaryPaymentToRaiseIds',
        () => t('loan.payment.moveCalendarDialog.errors.unnecessaryPaymentToRaiseIds'),
        (value, context) => {
          const fromDate = context.resolve<DateOnly>(ref('fromDate'))
          const toDate = context.resolve<DateOnly>(ref('toDate'))

          // If one of the dates isn't valid, we return true since we can't test
          if (!fromDate || !toDate) return true

          // We're moving in the futur, so we don't need to check
          if (fromDate.getTime() < toDate.getTime()) return true

          return value === 0
        },
      )
      .test(
        'test-needsPaymentToRaiseIds',
        () => t('loan.payment.moveCalendarDialog.errors.needsPaymentToRaiseIds'),
        (value, context) => {
          const fromDate = context.resolve<DateOnly>(ref('fromDate'))
          const toDate = context.resolve<DateOnly>(ref('toDate'))

          // If one of the dates isn't valid, we return true since we can't test
          if (!fromDate || !toDate) return true

          // He can override this validation
          if (user.rights.canMoveCalendarWithLateAmount) return true

          // Moving to the past, so no need to have payments to raise
          if (fromDate.getTime() > toDate.getTime()) return true

          return value !== 0
        },
      ),

    previewedSimulation: previewedSimulation(),
  })

export type MoveCalendarSchemaType = InferSubmit<ReturnType<typeof getMoveCalendarSchema>>

export const useMoveCalendarSchema = (simulation: CalendarSimulation) => {
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments
  return useMemo(() => getMoveCalendarSchema(simulation, user, canBypassAcpLock), [simulation, user, canBypassAcpLock])
}
